import {jobTimeToMinutes, parseApiTime} from '../jobs/job-schedule-format'
import type {DispatchJobSchedule} from './types'

export type DispatchScheduleInterval = {
    startDate: string
    endDate: string | null
    startMinutes: number | null
    endMinutes: number | null
    isCompleteSameDay: boolean
}

export type DispatchScheduleComparison = {
    overlaps: boolean
    /** `timed` is only used when both schedules are complete same-day windows. */
    mode: 'timed' | 'calendar'
    /** The booking's known end when it controls a timed same-day conflict. */
    release?: {date: string; time: string}
}

function validTimeMinutes(value: string | null): number | null {
    const normalized = parseApiTime(value)
    const minutes = jobTimeToMinutes(normalized)
    if (minutes == null || minutes < 0 || minutes > 23 * 60 + 59) {
        return null
    }
    return minutes
}

export function normalizeDispatchSchedule(schedule: DispatchJobSchedule): DispatchScheduleInterval {
    const startMinutes = validTimeMinutes(schedule.startTime)
    const endMinutes = validTimeMinutes(schedule.endTime)
    const isCompleteSameDay =
        schedule.endDate != null &&
        schedule.endDate === schedule.startDate &&
        startMinutes != null &&
        endMinutes != null

    return {
        startDate: schedule.startDate,
        endDate: schedule.endDate,
        startMinutes,
        endMinutes,
        isCompleteSameDay,
    }
}

function calendarRangesOverlap(
    left: DispatchScheduleInterval,
    right: DispatchScheduleInterval,
): boolean {
    const leftEnd = left.endDate ?? '9999-12-31'
    const rightEnd = right.endDate ?? '9999-12-31'
    return left.startDate <= rightEnd && right.startDate <= leftEnd
}

/**
 * Compares dispatch reservations. Complete same-day schedules use half-open
 * minute intervals; every other shape conservatively uses inclusive day ranges.
 */
export function compareDispatchSchedules(
    leftSchedule: DispatchJobSchedule,
    rightSchedule: DispatchJobSchedule,
): DispatchScheduleComparison {
    const left = normalizeDispatchSchedule(leftSchedule)
    const right = normalizeDispatchSchedule(rightSchedule)

    if (left.isCompleteSameDay && right.isCompleteSameDay) {
        if (left.startDate !== right.startDate) {
            return {overlaps: false, mode: 'timed'}
        }

        const overlaps =
            left.startMinutes! < right.endMinutes! && right.startMinutes! < left.endMinutes!
        return {
            overlaps,
            mode: 'timed',
            release: overlaps
                ? {
                      date: right.startDate,
                      time: `${Math.floor(right.endMinutes! / 60)
                          .toString()
                          .padStart(
                              2,
                              '0',
                          )}:${(right.endMinutes! % 60).toString().padStart(2, '0')}`,
                  }
                : undefined,
        }
    }

    return {
        overlaps: calendarRangesOverlap(left, right),
        mode: 'calendar',
    }
}
